<template>
  <div class="product-detail" :style="stickyCssVars">
    <template v-if="product">
      <div class="product-detail__shell">
        <nav class="product-detail__crumb">
          <router-link to="/mall">함우리몰</router-link>
          <AppIcon icon="mdi-chevron-right" :size="16" />
          <span>{{ product.category }}</span>
          <AppIcon icon="mdi-chevron-right" :size="16" />
          <em>{{ product.name }}</em>
        </nav>

        <div class="product-detail__layout">
            <div ref="galleryEl" class="product-detail__gallery">
              <div class="product-detail__gallery-stage">
                <ul v-if="gallery.length" class="product-detail__thumbs" aria-label="상품 이미지 목록">
                  <li v-for="(image, index) in gallery" :key="`${image}-${index}`">
                    <button
                      type="button"
                      :class="{ 'is-active': index === activeImageIndex }"
                      :aria-label="`${product.name} 이미지 ${index + 1}`"
                      @click="activeImageIndex = index"
                    >
                      <img :src="image" :alt="`${product.name} 이미지 ${index + 1}`" loading="lazy" />
                    </button>
                  </li>
                </ul>

                <div
                  class="product-detail__hero"
                  @touchstart.passive="onHeroTouchStart"
                  @touchend.passive="onHeroTouchEnd"
                >
                  <button
                    type="button"
                    class="product-detail__hero-btn"
                    aria-label="이미지 확대 보기"
                    @click="openZoom"
                  >
                    <img :src="activeImage" :alt="product.name" class="product-detail__hero-img" />
                  </button>
                  <span v-if="product.badge" class="product-detail__hero-badge">{{ product.badge }}</span>
                  <span class="product-detail__hero-unit">{{ imageCaption }}</span>
                  <span v-if="product.soldOut" class="product-detail__hero-soldout">품절</span>
                  <button
                    type="button"
                    class="product-detail__zoom-fab"
                    aria-label="이미지 확대 보기"
                    @click="openZoom"
                  >
                    <AppIcon icon="mdi-magnify-plus-outline" :size="20" />
                  </button>
                </div>
              </div>

              <div
                v-if="gallery.length > 1"
                class="product-detail__pager"
                aria-label="상품 이미지 넘기기"
              >
                <button
                  type="button"
                  class="product-detail__pager-btn"
                  aria-label="이전 사진"
                  :disabled="!canGoPrev"
                  @click="prevImage"
                >
                  <AppIcon icon="mdi-chevron-left" :size="22" />
                </button>
                <span class="product-detail__pager-count">
                  {{ activeImageIndex + 1 }} / {{ gallery.length }}
                </span>
                <button
                  type="button"
                  class="product-detail__pager-btn"
                  aria-label="다음 사진"
                  :disabled="!canGoNext"
                  @click="nextImage"
                >
                  <AppIcon icon="mdi-chevron-right" :size="22" />
                </button>
              </div>
            </div>

            <div
              v-if="zoomOpen"
              class="product-detail__zoom"
              role="dialog"
              aria-modal="true"
              aria-label="상품 이미지 확대"
              @click.self="closeZoom"
            >
              <button type="button" class="product-detail__zoom-close" aria-label="닫기" @click="closeZoom">
                <AppIcon icon="mdi-close" :size="22" />
              </button>
              <img
                :src="activeImage"
                :alt="product.name"
                class="product-detail__zoom-img"
                :style="{ transform: `scale(${zoomScale})` }"
                @wheel.prevent="onZoomWheel"
                @touchstart.passive="onPinchStart"
                @touchmove.passive="onPinchMove"
                @touchend.passive="onPinchEnd"
              />
            </div>

            <div class="product-detail__body">
            <div
              ref="tabsEl"
              class="product-detail__tabs"
              role="tablist"
              aria-label="상품 정보 탭"
            >
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                role="tab"
                class="product-detail__tab"
                :class="{ 'is-active': activeTab === tab.id }"
                :aria-selected="activeTab === tab.id"
                :id="`tab-${tab.id}`"
                :aria-controls="`panel-${tab.id}`"
                @click="scrollToTab(tab.id)"
              >
                {{ tab.label }}
                <template v-if="tab.id === 'review'"> {{ product.reviewCount }}</template>
              </button>
            </div>

            <div class="product-detail__info">
              <section
                id="panel-detail"
                class="product-detail__section"
                role="tabpanel"
                aria-labelledby="tab-detail"
              >
                <template v-if="product.highlights.length">
                  <div class="product-detail__specs-head">
                    <h2 id="product-specs-title">상품정보</h2>
                    <button
                      v-if="product.highlights.length > 4"
                      type="button"
                      class="product-detail__specs-toggle"
                      :aria-expanded="specsOpen"
                      aria-controls="product-specs"
                      @click="specsOpen = !specsOpen"
                    >
                      {{ specsOpen ? '접기' : `전체 ${product.highlights.length}개 보기` }}
                      <AppIcon :icon="specsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" :size="18" />
                    </button>
                  </div>
                  <dl
                    id="product-specs"
                    class="product-detail__specs"
                    :class="{ 'is-collapsed': product.highlights.length > 4 && !specsOpen }"
                    aria-labelledby="product-specs-title"
                  >
                    <div v-for="item in visibleHighlights" :key="item.label">
                      <dt>{{ item.label }}</dt>
                      <dd>{{ item.value }}</dd>
                    </div>
                  </dl>
                </template>

                <h2 class="product-detail__section-title">상세 설명</h2>
                <p v-if="product.description" class="product-detail__desc">{{ product.description }}</p>

                <div v-if="detailBlocks.length" class="product-detail__blocks">
                  <template v-for="(block, index) in detailBlocks" :key="index">
                    <h3 v-if="block.type === 'HEADING'" class="product-detail__block-heading">{{ block.text }}</h3>
                    <p v-else-if="block.type === 'TEXT'" class="product-detail__block-text">{{ block.text }}</p>
                    <p v-else-if="block.type === 'NOTICE'" class="product-detail__block-notice">
                      <AppIcon icon="mdi-information-outline" :size="16" />
                      {{ block.text }}
                    </p>
                    <figure v-else class="product-detail__block-image">
                      <img :src="block.imageUrl" :alt="block.alt || product.name" loading="lazy" />
                      <figcaption v-if="block.alt">{{ block.alt }}</figcaption>
                    </figure>
                  </template>
                </div>

                <ul class="product-detail__notice">
                  <li>{{ trustBadge }}</li>
                </ul>
                <div class="product-detail__notice-html" v-html="shippingNotesHtml" />
              </section>

              <section
                id="panel-review"
                class="product-detail__section"
                role="tabpanel"
                aria-labelledby="tab-review"
              >
                <h2>구매후기 {{ product.reviewCount }}</h2>

                <div v-if="canWriteReview" class="product-detail__write">
                  <p class="product-detail__write-lead">구매하신 상품에 대한 후기를 남겨 주세요.</p>
                  <label v-if="qnaOrders.length" class="product-detail__order-pick">
                    <span>관련 주문</span>
                    <select v-model="reviewForm.orderCode" :disabled="qnaOrdersLoading">
                      <option v-for="order in qnaOrders" :key="order.code" :value="order.code">
                        {{ formatQnaOrderOption(order) }}
                      </option>
                    </select>
                    <em>이 후기가 어느 주문에 대한 것인지 판매업체·관리자가 바로 봅니다.</em>
                  </label>
                  <div class="product-detail__stars-pick" role="group" aria-label="별점">
                    <button
                      v-for="star in 5"
                      :key="star"
                      type="button"
                      class="product-detail__star-btn"
                      :class="{ 'is-on': star <= reviewForm.rating }"
                      :aria-label="`${star}점`"
                      @click="reviewForm.rating = star"
                    >
                      <AppIcon :icon="star <= reviewForm.rating ? 'mdi-star' : 'mdi-star-outline'" :size="22" />
                    </button>
                  </div>
                  <textarea
                    v-model.trim="reviewForm.content"
                    class="product-detail__write-input"
                    rows="3"
                    maxlength="2000"
                    placeholder="배송 · 품질 · 포장 등 실제 사용 느낌을 적어 주세요."
                  />
                  <div class="product-detail__photo-pick">
                    <label class="product-detail__photo-btn">
                      <AppIcon icon="mdi-camera-outline" :size="18" />
                      {{ reviewImagePreview ? '사진 바꾸기' : '사진 첨부' }}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        hidden
                        @change="onReviewImagePick"
                      />
                    </label>
                    <button
                      v-if="reviewImagePreview"
                      type="button"
                      class="product-detail__photo-clear"
                      @click="clearReviewImage"
                    >
                      사진 삭제
                    </button>
                    <img
                      v-if="reviewImagePreview"
                      :src="reviewImagePreview"
                      alt="첨부 미리보기"
                      class="product-detail__photo-preview"
                    />
                  </div>
                  <button
                    type="button"
                    class="product-detail__btn product-detail__btn--solid"
                    :disabled="reviewBusy || !reviewForm.content"
                    @click="submitReview"
                  >
                    {{ reviewBusy ? '등록 중…' : '구매후기 등록' }}
                  </button>
                </div>
                <p v-else-if="myReview" class="product-detail__write-note">이미 구매후기를 작성하셨습니다.</p>
                <p v-else-if="isLive && isLoggedIn" class="product-detail__write-note">
                  입금이 확인된 구매 건이 있을 때만 구매후기를 남길 수 있습니다.
                </p>

                <div v-if="product.reviews.length" class="product-detail__review-tools">
                  <button
                    type="button"
                    class="product-detail__filter-chip"
                    :class="{ 'is-on': !photoReviewsOnly }"
                    @click="photoReviewsOnly = false"
                  >
                    전체 {{ product.reviews.length }}
                  </button>
                  <button
                    type="button"
                    class="product-detail__filter-chip"
                    :class="{ 'is-on': photoReviewsOnly }"
                    :disabled="!photoReviewCount"
                    @click="photoReviewsOnly = true"
                  >
                    포토후기 {{ photoReviewCount }}
                  </button>
                </div>

                <div v-if="visibleReviews.length" class="product-detail__reviews">
                  <article v-for="review in visibleReviews" :key="review.id" class="product-detail__review">
                    <header>
                      <strong>{{ review.author }}</strong>
                      <span>{{ review.org }}</span>
                      <em>
                        <AppIcon icon="mdi-star" :size="14" />
                        {{ review.rating.toFixed(1) }}
                      </em>
                      <time>{{ review.date }}</time>
                    </header>
                    <p>{{ review.content }}</p>
                    <a
                      v-if="review.image"
                      :href="review.image"
                      class="product-detail__review-photo"
                      target="_blank"
                      rel="noopener"
                    >
                      <img :src="review.image" alt="후기 사진" />
                    </a>
                    <div v-if="review.reply" class="product-detail__reply">
                      <strong>판매업체 답변</strong>
                      <p>{{ review.reply }}</p>
                    </div>
                  </article>
                </div>
                <p v-else class="product-detail__empty-review">
                  {{ photoReviewsOnly ? '포토후기가 없습니다.' : '등록된 구매후기가 없습니다.' }}
                </p>
              </section>

              <section
                id="panel-qna"
                class="product-detail__section"
                role="tabpanel"
                aria-labelledby="tab-qna"
              >
                <h2>상품문의 {{ (product.qnas || []).length }}</h2>

                <div v-if="isLoggedIn" class="product-detail__write">
                  <p class="product-detail__write-lead">
                    이 상품이 들어간 주문을 고른 뒤 문의를 남깁니다. 그 밖 문의도 문의 내역에서 작성합니다.
                  </p>
                  <p class="product-detail__write-note">
                    <router-link to="/mall/mypage/consults">문의 내역</router-link>
                  </p>
                  <label class="product-detail__order-pick">
                    <span>관련 주문</span>
                    <select v-model="qnaForm.orderCode" :disabled="qnaOrdersLoading || !qnaOrders.length">
                      <option value="" disabled>주문내역에서 고르세요</option>
                      <option v-for="order in qnaOrders" :key="order.code" :value="order.code">
                        {{ formatQnaOrderOption(order) }}
                      </option>
                    </select>
                    <em v-if="qnaOrdersLoading">주문 목록을 불러오는 중…</em>
                    <em v-else-if="!qnaOrders.length">
                      이 상품이 들어 있는 주문이 없습니다. 문의 내역에서 주문번호를 첨부해 작성해 주세요.
                    </em>
                  </label>
                  <template v-if="qnaForm.orderCode">
                    <textarea
                      v-model.trim="qnaForm.question"
                      class="product-detail__write-input"
                      rows="3"
                      maxlength="2000"
                      placeholder="선택한 주문에 대해 궁금한 점을 적어 주세요."
                    />
                    <label class="product-detail__secret">
                      <input v-model="qnaForm.secret" type="checkbox" />
                      비밀글로 문의하기
                    </label>
                    <button
                      type="button"
                      class="product-detail__btn product-detail__btn--solid"
                      :disabled="qnaBusy || !qnaForm.question || !qnaForm.orderCode"
                      @click="submitQna"
                    >
                      {{ qnaBusy ? '등록 중…' : '문의 등록' }}
                    </button>
                  </template>
                </div>
                <p v-else class="product-detail__write-note">
                  문의는 로그인 후 작성할 수 있습니다.
                  <router-link :to="loginTo">로그인하기</router-link>
                </p>

                <div v-if="(product.qnas || []).length" class="product-detail__reviews">
                  <article v-for="item in product.qnas || []" :key="item.id" class="product-detail__review">
                    <header>
                      <strong>{{ item.author }}</strong>
                      <span>{{ item.org }}</span>
                      <span v-if="item.secret" class="product-detail__secret-tag">비밀글</span>
                      <time>{{ item.date }}</time>
                    </header>
                    <p>{{ qnaVisibleText(item) }}</p>
                    <template v-if="canSeeQna(item)">
                      <div v-if="item.answer" class="product-detail__reply">
                        <strong>답변</strong>
                        <p>{{ item.answer }}</p>
                        <em v-if="item.answeredAt">{{ item.answeredAt }}</em>
                      </div>
                      <p v-else class="product-detail__wait">답변 대기 중</p>
                    </template>
                  </article>
                </div>
                <p v-else class="product-detail__empty-review">등록된 문의가 없습니다.</p>
              </section>

              <section
                id="panel-shipping"
                class="product-detail__section"
                role="tabpanel"
                aria-labelledby="tab-shipping"
              >
                <h2>배송 · 교환 · 반품</h2>
                <dl class="product-detail__ship-policy">
                  <div>
                    <dt>배송비</dt>
                    <dd>{{ productShippingLabel }}</dd>
                  </div>
                  <div v-if="product.leadTime">
                    <dt>출고</dt>
                    <dd>{{ product.leadTime }}</dd>
                  </div>
                  <div>
                    <dt>수령</dt>
                    <dd>{{ MALL_FULFILLMENT_LIMIT }}</dd>
                  </div>
                  <div v-if="product.origin">
                    <dt>원산지</dt>
                    <dd>{{ product.origin }}</dd>
                  </div>
                  <div v-if="sameDayCutoffText">
                    <dt>당일 출고</dt>
                    <dd>{{ sameDayCutoffText }}</dd>
                  </div>
                  <div v-if="hasStoreReturnFee">
                    <dt>반품 배송비</dt>
                    <dd>{{ returnShippingLabel }}</dd>
                  </div>
                </dl>
                <ul
                  v-if="storeReturnPolicyLines.length"
                  class="product-detail__policy-list"
                >
                  <li v-for="(line, index) in storeReturnPolicyLines" :key="`pol-${index}`">
                    {{ line }}
                  </li>
                </ul>
                <div
                  v-else
                  class="product-detail__notice-html"
                  v-html="returnPolicyHtml"
                />
              </section>
            </div>

            <section v-if="product.soldOut && substitutes.length" class="product-detail__related">
              <h2>관련상품</h2>
              <p class="product-detail__related-lead">품절 상품의 대체 가능한 관련상품입니다.</p>
              <div class="product-detail__related-grid">
                <ProductCard v-for="item in substitutes" :key="`sub-${item.id}`" :product="item" />
              </div>
            </section>

            <section v-if="related.length" class="product-detail__related">
              <h2>관련상품</h2>
              <div class="product-detail__related-grid">
                <ProductCard v-for="item in related" :key="item.id" :product="item" />
              </div>
            </section>

            <section v-if="alsoRecommend.length" class="product-detail__related">
              <h2>추천상품</h2>
              <div class="product-detail__related-grid">
                <ProductCard
                  v-for="item in alsoRecommend"
                  :key="`rec-${item.id}`"
                  :product="item"
                />
              </div>
            </section>

            <section v-if="recentOthers.length" class="product-detail__related">
              <h2>오늘 본 상품</h2>
              <div class="product-detail__related-grid">
                <ProductCard
                  v-for="item in recentOthers"
                  :key="`rv-${item.id}`"
                  :product="item"
                />
              </div>
            </section>
            </div>

          <!-- 우측 구매 영역 — 스크롤해도 따라온다 -->
          <aside ref="buyEl" class="product-detail__buy" aria-label="구매 정보">
            <div class="product-detail__buy-card">
              <div class="product-detail__buy-scroll">
              <div class="product-detail__buy-block">
                <div class="product-detail__store-row">
                  <router-link
                    v-if="product.storeName && product.sellerCode"
                    :to="mallStorePath(product.sellerCode)"
                    class="product-detail__store"
                  >
                    {{ product.storeName }}
                    <AppIcon icon="mdi-chevron-right" :size="16" />
                  </router-link>
                  <p v-else-if="product.storeName" class="product-detail__store">{{ product.storeName }}</p>
                  <div class="product-detail__tools">
                    <button type="button" class="product-detail__compare" @click="onShare">
                      공유하기
                    </button>
                    <button
                      type="button"
                      class="product-detail__compare"
                      :class="{ 'is-on': wished }"
                      @click="onWish"
                    >
                      찜하기
                    </button>
                  </div>
                </div>

                <h1 class="product-detail__title">{{ product.name }}</h1>

                <button type="button" class="product-detail__rating" @click="scrollToTab('review')">
                  <span class="product-detail__stars" aria-hidden="true">
                    <AppIcon
                      v-for="star in 5"
                      :key="star"
                      :icon="star <= Math.round(product.rating) ? 'mdi-star' : 'mdi-star-outline'"
                      :size="15"
                    />
                  </span>
                  <strong>{{ product.rating.toFixed(1) }}</strong>
                  <em>구매후기 {{ formatPrice(product.reviewCount) }}</em>
                </button>
              </div>

              <div class="product-detail__buy-block">
                <div class="product-detail__price-row">
                  <span v-if="isGroupBuy" class="product-detail__amount">{{ formatPrice(cartonPrice) }}<em>원</em><small>/카톤</small></span>
                  <span v-else class="product-detail__amount">{{ formatPrice(priced.unitPrice) }}<em>원</em><small>/{{ sellNoun }}</small></span>
                </div>
                <p v-if="isGroupBuy" class="product-detail__unit">{{ cartonPieces }}개 묶음 · 1개당 {{ formatPrice(priced.unitPrice) }}원</p>
                <p v-else class="product-detail__unit">{{ sellSummary }}<template v-if="product.unit"> · {{ product.unit }}</template></p>
                <p class="product-detail__stock" :class="{ 'is-out': product.soldOut || stockQty <= 0 }">
                  <template v-if="product.soldOut || stockQty <= 0">품절</template>
                  <template v-else>재고 {{ formatStockCount(stockQty, product.sellUnit) }}</template>
                </p>
              </div>

              <div class="product-detail__buy-block">
                <dl class="product-detail__buy-facts">
                  <div>
                    <dt>배송비</dt>
                    <dd>{{ productShippingLabel }}</dd>
                  </div>
                  <div v-if="product.leadTime">
                    <dt>출고</dt>
                    <dd>{{ product.leadTime }}</dd>
                  </div>
                  <div>
                    <dt>수령</dt>
                    <dd>{{ MALL_FULFILLMENT_LIMIT }}</dd>
                  </div>
                  <div v-if="product.origin">
                    <dt>원산지</dt>
                    <dd>{{ product.origin }}</dd>
                  </div>
                  <div v-if="sameDayCutoffText">
                    <dt>당일 출고</dt>
                    <dd>{{ sameDayCutoffText }}</dd>
                  </div>
                </dl>
              </div>

              <!-- 옵션은 하나만. 수량은 그 옵션만 올리고, 옵션을 바꾸면 1부터 다시 센다 -->
              <div class="product-detail__buy-block">
                <div class="product-detail__options">
                  <label v-if="!isGroupBuy" class="product-detail__option-label" for="pdp-option-pick">
                    옵션 선택 <em>(필수)</em>
                  </label>
                  <select
                    v-if="!isGroupBuy"
                    id="pdp-option-pick"
                    v-model="optionPick"
                    class="product-detail__option-select"
                    :disabled="product.soldOut || !flatOptionChoices.length"
                    @change="onOptionPick"
                  >
                    <option v-if="!hasBasePriceChoice" value="">옵션을 선택하세요</option>
                    <option
                      v-for="choice in flatOptionChoices"
                      :key="choice.name"
                      :value="choice.name"
                      :disabled="!isChoiceAvailable(choice)"
                    >
                      {{ choiceLabel(choice) }}
                    </option>
                  </select>

                  <MallOrderPackMark
                    v-if="isPack && !isGroupBuy"
                    size="detail"
                    :order-step="orderStep"
                    :sell-unit="product.sellUnit"
                  />

                  <ul v-if="selectedLines.length" class="product-detail__picks">
                    <li v-for="line in selectedLines" :key="line.key">
                      <div class="product-detail__pick-meta">
                        <strong>{{ isGroupBuy ? '1카톤' : line.optionName }}</strong>
                        <span v-if="isGroupBuy">{{ formatPrice(lineUnitPrice(line.optionExtra) * cartonPieces) }}원 × {{ cartonCount(line.qty) }}</span>
                        <span v-else>{{ formatPrice(lineUnitPrice(line.optionExtra)) }}원 × {{ line.qty }}</span>
                      </div>
                      <div class="product-detail__pick-ops">
                        <QtyStepper
                          :model-value="line.qty"
                          :min="minQty"
                          :max="maxQty"
                          :step="orderStep"
                          :unit="sellNoun"
                          :carton="isGroupBuy"
                          @update:model-value="setLineQty(line.key, $event)"
                        />
                      </div>
                    </li>
                  </ul>

                  <button
                    v-if="lastPurchaseQty && selectedLines.length"
                    type="button"
                    class="product-detail__last-qty"
                    @click="applyLastPurchase"
                  >
                    지난번 {{ isGroupBuy ? `${cartonCount(lastPurchaseQty)}카톤` : `${lastPurchaseQty}${sellNoun}` }}로 맞추기
                  </button>
                  <p v-if="qtyHint" class="product-detail__qty-hint">{{ qtyHint }}</p>
                </div>
              </div>
              </div>

              <div class="product-detail__buy-block product-detail__buy-block--foot">
                <div class="product-detail__total">
                  <div class="product-detail__total-copy">
                    <span>주문금액</span>
                    <em v-if="orderShippingFee > 0">
                      주문 배송비 {{ formatPrice(orderShippingFee) }}원 포함
                    </em>
                    <em v-else>주문 배송비 무료</em>
                  </div>
                  <strong>{{ formatPrice(totalAmount) }}<em>원</em></strong>
                </div>

                <div class="product-detail__actions">
                  <button
                    v-if="product.soldOut"
                    type="button"
                    class="product-detail__cta product-detail__cta--restock"
                    :class="{ 'is-on': restockSubscribed }"
                    :disabled="restockBusy"
                    @click="toggleRestock"
                  >
                    {{ restockSubscribed ? '재입고 알림 신청됨' : '재입고 알림 신청' }}
                  </button>
                  <button
                    v-else
                    type="button"
                    class="product-detail__cta product-detail__cta--buy"
                    @click="buyNow"
                  >
                    바로구매
                  </button>
                  <div class="product-detail__action-row">
                    <button
                      type="button"
                      class="product-detail__tool"
                      :class="{ 'is-active': wished }"
                      @click="onWish"
                    >
                      <AppIcon :icon="wished ? 'mdi-heart' : 'mdi-heart-outline'" :size="20" />
                      찜하기
                    </button>
                    <button
                      type="button"
                      class="product-detail__tool"
                      :disabled="product.soldOut"
                      @click="addToCart"
                    >
                      <AppIcon icon="mdi-cart-outline" :size="20" />
                      장바구니
                    </button>
                  </div>
                </div>

                <p v-if="!isLoggedIn" class="product-detail__login-note">
                  장바구니 · 찜하기 · 바로구매는 로그인 후 이용할 수 있습니다.
                </p>
              </div>
            </div>

            <transition name="detail-toast">
              <p v-if="toast" class="product-detail__toast">
                <AppIcon icon="mdi-check-circle" :size="18" />
                {{ toast }}
              </p>
            </transition>
          </aside>
        </div>
      </div>

      <div class="product-detail__sticky" aria-label="구매 바로가기">
        <button
          type="button"
          class="product-detail__like"
          :class="{ 'is-active': wished }"
          aria-label="찜하기"
          @click="onWish"
        >
          <AppIcon :icon="wished ? 'mdi-heart' : 'mdi-heart-outline'" :size="22" />
        </button>
        <button type="button" class="product-detail__cart" :disabled="product.soldOut" @click="addToCart">
          장바구니
        </button>
        <button
          v-if="product.soldOut"
          type="button"
          class="product-detail__buy-btn product-detail__buy-btn--restock"
          :class="{ 'is-on': restockSubscribed }"
          :disabled="restockBusy"
          @click="toggleRestock"
        >
          {{ restockSubscribed ? '알림 신청됨' : '재입고 알림' }}
        </button>
        <button v-else type="button" class="product-detail__buy-btn" @click="buyNow">바로구매</button>
      </div>
    </template>

    <div v-else-if="loading" class="product-detail__missing">
      <AppIcon icon="mdi-loading" :size="48" />
      <p>상품을 불러오는 중입니다…</p>
    </div>

    <div v-else class="product-detail__missing">
      <AppIcon icon="mdi-package-variant" :size="64" />
      <p>해당 상품을 찾을 수 없습니다.</p>
      <router-link to="/mall" class="product-detail__missing-link">함우리몰 홈으로</router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductCard from '@/components/mall/ProductCard.vue'
import QtyStepper from '@/components/mall/QtyStepper.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { setBuyNowKeys } from '@/composables/useBuyNowCheckout'
import { cartKey, useCart } from '@/composables/useCart'
import { useCartSelection } from '@/composables/useCartSelection'
import { useMallAuth } from '@/composables/useMallAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useRecentlyViewed } from '@/composables/useRecentlyViewed'
import { useWishlist } from '@/composables/useWishlist'
import { fetchMallProduct } from '@/api/mall'
import { fetchLastPurchaseQty, MALL_ORDER_STATUS_LABEL } from '@/api/mallOrder'
import {
  fetchRestockNotifyStatus,
  subscribeRestockNotify,
  unsubscribeRestockNotify,
} from '@/api/mallProduct'
import {
  createProductQna,
  createProductReview,
  fetchMyReviewStatus,
  fetchQnaOrders,
  uploadMemberImages,
  type MallQnaOrderOption,
} from '@/api/mallReview'
import { MALL_FULFILLMENT_LIMIT, mallStorePath } from '@/constants/mall'
import { memberLoginTo } from '@/utils/authRedirect'
import { getAllProducts, formatPrice, type MallProduct, type MallProductOption } from '@/constants/mallProducts'
import { useContentSnippets, useSameDayCutoffText, SNIPPET_FALLBACKS } from '@/composables/useContentSnippets'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import { formatShippingFeeAmount, toShippingLine } from '@/utils/mallShipping'
import {
  calcOrderShippingFromLines,
  formatMinOrderLabel,
  formatShippingChargeLabel,
  formatStockCount,
  imageShowsCaption,
  isOrderPack,
  optionPieceCount,
  sellUnitNoun,
  sellUnitSummary,
  snapOrderQty,
} from '@/utils/mallSell'

const route = useRoute()
const router = useRouter()
const loginTo = computed(() => memberLoginTo(route.fullPath))
const { addItem } = useCart()
const { selectOnly } = useCartSelection()
const { isWished, toggleWish } = useWishlist()
const { ensureLoggedIn } = useMallAuth()
const { isLoggedIn } = useMemberAuth()
const { recentIds, pushRecent } = useRecentlyViewed()
const { loadSnippets, snippet } = useContentSnippets()

const trustBadge = computed(() => snippet('mall.product.trust_badge') || SNIPPET_FALLBACKS['mall.product.trust_badge'])
const shippingNotesHtml = computed(
  () => snippet('mall.product.shipping_notes') || SNIPPET_FALLBACKS['mall.product.shipping_notes'],
)
const returnPolicyHtml = computed(
  () => snippet('mall.product.return_policy') || SNIPPET_FALLBACKS['mall.product.return_policy'],
)
const storeReturnPolicyLines = computed(() =>
  String(product.value?.storeReturnPolicy || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean),
)

const tabs = [
  { id: 'detail', label: '상세정보' },
  { id: 'review', label: '구매후기' },
  { id: 'qna', label: '상품문의' },
  { id: 'shipping', label: '배송/교환' },
] as const

type TabId = (typeof tabs)[number]['id']

const activeTab = ref<TabId>('detail')
const tabsEl = ref<HTMLElement | null>(null)
const galleryEl = ref<HTMLElement | null>(null)
const buyEl = ref<HTMLElement | null>(null)
/** 탭 높이만 페이지에서 잡고, 헤더 offset 은 MallHeader 가 전역으로 공개한다. */
const stickyCssVars = computed(() => ({
  '--mall-tab-offset': `${tabsEl.value?.offsetHeight ?? 52}px`,
}))
/** 탭 클릭으로 스크롤 중일 때는 spy가 탭을 되돌려 놓지 않게 한다 */
let scrollingByClick = false
let scrollUnlockTimer: ReturnType<typeof setTimeout> | undefined
let scrollSpyRaf = 0
let buyResizeObserver: ResizeObserver | null = null

function headerOffsetPx() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--mall-header-offset').trim()
  const n = Number.parseFloat(raw)
  return Number.isFinite(n) ? n : 0
}

function mallCssZoom(): number {
  const app = document.querySelector('.site-app--mall')
  const raw = getComputedStyle(app || document.documentElement).getPropertyValue('--mall-ui-zoom').trim()
  const n = Number.parseFloat(raw)
  return Number.isFinite(n) && n > 0 ? n : 1
}

/** 큰 모니터 CSS zoom · 브라우저 배율 · 빵부스러기까지 반영해 구매 카드를 화면 안에 가둔다 */
function fitBuyPanelToViewport() {
  const card = buyEl.value?.querySelector('.product-detail__buy-card') as HTMLElement | null
  if (!card) return
  if (window.innerWidth <= 960) {
    card.style.removeProperty('--buy-max-h')
    return
  }

  const visualTop = card.getBoundingClientRect().top
  const visualView = window.visualViewport?.height ?? window.innerHeight
  const visualRemain = visualView - visualTop - 12
  const visualH = card.getBoundingClientRect().height
  const layoutH = card.offsetHeight
  const zoom = visualH > 1 && layoutH > 1 ? visualH / layoutH : mallCssZoom()
  const layoutMax = Math.max(280, Math.floor(visualRemain / zoom))
  card.style.setProperty('--buy-max-h', `${layoutMax}px`)
}

/** 대표 사진 세로를 오른쪽 구매 카드에 맞춘 정사각으로 둔다 */
function syncGalleryHeight() {
  const gallery = galleryEl.value
  const buyCard = buyEl.value?.querySelector('.product-detail__buy-card') as HTMLElement | null
  if (!gallery) return

  if (!buyCard || window.innerWidth <= 960) {
    gallery.style.removeProperty('--gallery-hero-height')
    return
  }

  const thumbs = gallery.querySelector('.product-detail__thumbs') as HTMLElement | null
  const thumbsW = thumbs ? Math.ceil(thumbs.getBoundingClientRect().width) + 12 : 0
  const buyH = Math.round(buyCard.getBoundingClientRect().height)
  const colW = gallery.parentElement?.clientWidth || buyH
  const size = Math.max(280, Math.min(buyH, colW - thumbsW))
  gallery.style.setProperty('--gallery-hero-height', `${size}px`)
}

function bindGalleryHeightSync() {
  buyResizeObserver?.disconnect()
  buyResizeObserver = null
  const buyCard = buyEl.value?.querySelector('.product-detail__buy-card')
  if (!buyCard || typeof ResizeObserver === 'undefined') {
    fitBuyPanelToViewport()
    syncGalleryHeight()
    return
  }
  buyResizeObserver = new ResizeObserver(() => {
    syncGalleryHeight()
  })
  buyResizeObserver.observe(buyCard)
  fitBuyPanelToViewport()
  syncGalleryHeight()
}

const qty = ref(1)
const lastPurchaseQty = ref<number | null>(null)
const specsOpen = ref(false)
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined

/* 판매업체가 올린 상품은 서버에서 받아야 상세 블록·후기까지 나온다. */
const product = ref<MallProduct | null>(null)
const { sameDayCutoffText } = useSameDayCutoffText(() => product.value?.storeCutoff)
const hasStoreReturnFee = computed(
  () => Boolean(product.value?.sellerCode),
)
const sellNoun = computed(() => sellUnitNoun(product.value?.sellUnit))
const imageCaption = computed(() =>
  product.value ? imageShowsCaption(product.value) : '사진은 상품 1개입니다',
)
const sellSummary = computed(() => (product.value ? sellUnitSummary(product.value) : '1개'))
const productShippingLabel = computed(() => {
  const label = product.value ? formatShippingChargeLabel(product.value) : formatShippingFeeAmount(0)
  return label === '무료' ? '무료배송' : label
})
const returnShippingLabel = computed(() => {
  const every = Math.max(1, Number(product.value?.returnEvery) || 10)
  const fee = Math.max(0, Number(product.value?.returnFee) || 0)
  if (!fee) return '없음'
  const noun = sellNoun.value
  const money = fee.toLocaleString('ko-KR')
  return `${every}${noun}당 ${money}원 · ${every}${noun} 이하는 ${money}원`
})

const minQty = computed(() => Math.max(1, product.value?.minOrder || 1))
const orderStep = computed(() => Math.max(1, product.value?.orderStep || 1))
const isPack = computed(() => isOrderPack(orderStep.value))
const isGroupBuy = computed(() => product.value?.mallKind === 'SALE')
const cartonPieces = computed(() => Math.max(1, product.value?.packQty || orderStep.value || 1))
const cartonPrice = computed(() => priced.value.unitPrice * cartonPieces.value)
function cartonCount(qty: number) {
  return Math.max(1, Math.round((Number(qty) || cartonPieces.value) / cartonPieces.value))
}
const stockQty = computed(() => Math.max(0, Number(product.value?.stock) || 0))
const maxQty = computed(() => {
  const stock = stockQty.value
  /* 주문 상한은 재고만. */
  return Math.max(minQty.value, stock > 0 ? stock : minQty.value)
})

const qtyHint = computed(() => {
  if (product.value?.soldOut || stockQty.value <= 0) return '품절'
  if (isPack.value) return ''
  if (minQty.value > 1) return formatMinOrderLabel(minQty.value, product.value?.sellUnit)
  return ''
})

function clampQty(value: number) {
  return snapOrderQty(value, minQty.value, orderStep.value, maxQty.value)
}

async function loadLastPurchase(productId: string) {
  lastPurchaseQty.value = null
  if (!isLoggedIn.value || !productId) return
  try {
    const qtyValue = await fetchLastPurchaseQty(productId)
    lastPurchaseQty.value = qtyValue && qtyValue > 0 ? qtyValue : null
  } catch {
    lastPurchaseQty.value = null
  }
}

function applyLastPurchase() {
  if (!lastPurchaseQty.value) return
  const next = clampQty(lastPurchaseQty.value)
  qty.value = next
  if (selectedLines.value.length) {
    selectedLines.value = selectedLines.value.map((line) => ({ ...line, qty: next }))
  }
}

const visibleHighlights = computed(() => {
  const list = product.value?.highlights || []
  if (specsOpen.value || list.length <= 4) return list
  return list.slice(0, 4)
})
const loading = ref(true)
const activeImageIndex = ref(0)
const zoomOpen = ref(false)
const zoomScale = ref(1)
let touchStartX = 0
let swipeMoved = false
let pinchStartDist = 0
let pinchStartScale = 1

function openZoom() {
  if (swipeMoved) {
    swipeMoved = false
    return
  }
  zoomOpen.value = true
  zoomScale.value = 1
}

function closeZoom() {
  zoomOpen.value = false
  zoomScale.value = 1
}

const canGoPrev = computed(() => gallery.value.length > 1 && activeImageIndex.value > 0)
const canGoNext = computed(
  () => gallery.value.length > 1 && activeImageIndex.value < gallery.value.length - 1,
)

function prevImage() {
  if (!canGoPrev.value) return
  activeImageIndex.value -= 1
}

function nextImage() {
  if (!canGoNext.value) return
  activeImageIndex.value += 1
}

function onHeroTouchStart(event: TouchEvent) {
  swipeMoved = false
  touchStartX = event.changedTouches[0]?.clientX ?? 0
}

function onHeroTouchEnd(event: TouchEvent) {
  if (gallery.value.length < 2) return
  const endX = event.changedTouches[0]?.clientX ?? touchStartX
  const delta = endX - touchStartX
  if (Math.abs(delta) < 40) return
  swipeMoved = true
  if (delta < 0) nextImage()
  else prevImage()
}

function onZoomWheel(event: WheelEvent) {
  const next = zoomScale.value + (event.deltaY < 0 ? 0.12 : -0.12)
  zoomScale.value = Math.min(3, Math.max(1, next))
}

function pinchDistance(event: TouchEvent) {
  if (event.touches.length < 2) return 0
  const a = event.touches[0]
  const b = event.touches[1]
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function onPinchStart(event: TouchEvent) {
  if (event.touches.length < 2) return
  pinchStartDist = pinchDistance(event)
  pinchStartScale = zoomScale.value
}

function onPinchMove(event: TouchEvent) {
  if (event.touches.length < 2 || !pinchStartDist) return
  const ratio = pinchDistance(event) / pinchStartDist
  zoomScale.value = Math.min(3, Math.max(1, pinchStartScale * ratio))
}

function onPinchEnd() {
  pinchStartDist = 0
}

const canReview = ref(false)
const myReview = ref(false)
const reviewBusy = ref(false)
const qnaBusy = ref(false)
const reviewForm = reactive({ rating: 5, content: '', orderCode: '' })
const reviewImageFile = ref<File | null>(null)
const reviewImagePreview = ref('')
const photoReviewsOnly = ref(false)
const qnaForm = reactive({ kind: '' as '' | 'order' | 'general', question: '', secret: false, orderCode: '' })
const qnaOrders = ref<MallQnaOrderOption[]>([])
const qnaOrdersLoading = ref(false)
const restockSubscribed = ref(false)
const restockBusy = ref(false)

function formatQnaOrderOption(order: MallQnaOrderOption) {
  const when = String(order.orderedAt || '').slice(0, 10)
  const status =
    MALL_ORDER_STATUS_LABEL[order.status as keyof typeof MALL_ORDER_STATUS_LABEL] || order.status
  const summary = order.itemSummary || '주문 상품'
  return `${when} · ${order.code} · ${status} · ${summary}`
}

async function loadQnaOrders() {
  if (!isLoggedIn.value || !product.value?.id?.startsWith('MP')) {
    qnaOrders.value = []
    qnaForm.orderCode = ''
    return
  }
  qnaOrdersLoading.value = true
  try {
    qnaOrders.value = await fetchQnaOrders(product.value.id)
    const fromQuery = String(route.query.order || '').trim()
    if (fromQuery && qnaOrders.value.some((row) => row.code === fromQuery)) {
      qnaForm.orderCode = fromQuery
    } else if (qnaForm.orderCode && !qnaOrders.value.some((row) => row.code === qnaForm.orderCode)) {
      qnaForm.orderCode = ''
    }
    pickDefaultQnaOrder()
    if (!reviewForm.orderCode || !qnaOrders.value.some((row) => row.code === reviewForm.orderCode)) {
      reviewForm.orderCode = qnaOrders.value[0]?.code || ''
    }
  } catch {
    qnaOrders.value = []
  } finally {
    qnaOrdersLoading.value = false
  }
}

function pickDefaultQnaOrder() {
  if (qnaForm.orderCode && qnaOrders.value.some((row) => row.code === qnaForm.orderCode)) return
  qnaForm.orderCode = qnaOrders.value[0]?.code || ''
}

const isLive = computed(() => Boolean(product.value?.storeName || product.value?.id?.startsWith('MP')))
const canWriteReview = computed(() => isLive.value && isLoggedIn.value && canReview.value && !myReview.value)
const photoReviewCount = computed(
  () => product.value?.reviews.filter((review) => Boolean(review.image)).length || 0,
)
const visibleReviews = computed(() => {
  const list = product.value?.reviews || []
  return photoReviewsOnly.value ? list.filter((review) => Boolean(review.image)) : list
})

function canSeeQna(item: { secret?: boolean; question: string }) {
  return !item.secret || item.question !== '비밀 문의입니다.'
}

function qnaVisibleText(item: { question: string }) {
  return item.question
}

function onReviewImagePick(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  if (file.size > 5 * 1024 * 1024) {
    showToast('사진은 5MB 이하만 첨부할 수 있습니다.')
    return
  }
  if (reviewImagePreview.value) URL.revokeObjectURL(reviewImagePreview.value)
  reviewImageFile.value = file
  reviewImagePreview.value = URL.createObjectURL(file)
}

function clearReviewImage() {
  if (reviewImagePreview.value) URL.revokeObjectURL(reviewImagePreview.value)
  reviewImageFile.value = null
  reviewImagePreview.value = ''
}

async function loadRestockStatus(productId: string, soldOut: boolean) {
  restockSubscribed.value = false
  if (!soldOut || !isLoggedIn.value || !productId.startsWith('MP')) return
  try {
    restockSubscribed.value = await fetchRestockNotifyStatus(productId)
  } catch {
    restockSubscribed.value = false
  }
}

async function toggleRestock() {
  if (!product.value || !ensureLoggedIn() || restockBusy.value) return
  restockBusy.value = true
  try {
    if (restockSubscribed.value) {
      await unsubscribeRestockNotify(product.value.id)
      restockSubscribed.value = false
      showToast('재입고 알림 신청을 취소했습니다.')
    } else {
      await subscribeRestockNotify(product.value.id)
      restockSubscribed.value = true
      showToast('재입고되면 알려 드리겠습니다.')
    }
  } catch (error) {
    showToast(error instanceof Error ? error.message : '재입고 알림 처리에 실패했습니다.')
  } finally {
    restockBusy.value = false
  }
}

async function loadReviewStatus(productId: string) {
  canReview.value = false
  myReview.value = false
  if (!isLoggedIn.value || !productId.startsWith('MP')) {
    return
  }

  try {
    const status = await fetchMyReviewStatus(productId)
    canReview.value = status.canReview && !status.myReview
    myReview.value = Boolean(status.myReview)
  } catch {
    canReview.value = false
  }
}

async function loadProduct() {
  loading.value = true
  activeImageIndex.value = 0
  specsOpen.value = false
  lastPurchaseQty.value = null
  product.value = await fetchMallProduct(route.params.id as string)
  loading.value = false
  if (product.value) {
    qty.value = clampQty(minQty.value)
    pushRecent(product.value.id)
    await Promise.all([
      loadReviewStatus(product.value.id),
      loadRestockStatus(product.value.id, Boolean(product.value.soldOut)),
      loadLastPurchase(product.value.id),
      loadQnaOrders(),
    ])
  }
}

void loadProduct().then(() => {
  void nextTick(() => {
    bindGalleryHeightSync()
    if (route.query.tab) applyTabQuery(true)
    else syncActiveTabByScroll()
  })
})

async function submitReview() {
  if (!product.value || !ensureLoggedIn() || reviewBusy.value) return
  reviewBusy.value = true
  try {
    let image = ''
    if (reviewImageFile.value) {
      const uploaded = await uploadMemberImages([reviewImageFile.value])
      image = uploaded[0]?.url || ''
    }
    const review = await createProductReview(product.value.id, {
      rating: reviewForm.rating,
      content: reviewForm.content,
      image: image || undefined,
      orderCode: reviewForm.orderCode || undefined,
    })
    const prevCount = product.value.reviewCount
    const nextCount = prevCount + 1
    const nextRating =
      Math.round(((product.value.rating * prevCount + review.rating) / nextCount) * 10) / 10
    product.value = {
      ...product.value,
      reviews: [review, ...product.value.reviews],
      reviewCount: nextCount,
      rating: nextRating,
    }
    reviewForm.content = ''
    reviewForm.orderCode = ''
    clearReviewImage()
    canReview.value = false
    myReview.value = true
    showToast('구매후기를 등록했습니다.')
  } catch (error) {
    showToast(error instanceof Error ? error.message : '구매후기 등록에 실패했습니다.')
  } finally {
    reviewBusy.value = false
  }
}

async function submitQna() {
  if (!product.value || !ensureLoggedIn() || qnaBusy.value) return
  if (!product.value.id.startsWith('MP')) {
    showToast('이 상품에는 문의를 남길 수 없습니다.')
    return
  }
  if (!qnaForm.orderCode) {
    showToast('주문내역에서 해당 주문을 골라 주세요.')
    return
  }

  qnaBusy.value = true
  try {
    const qna = await createProductQna(product.value.id, {
      question: qnaForm.question,
      secret: qnaForm.secret,
      orderCode: qnaForm.orderCode,
    })
    product.value = {
      ...product.value,
      qnas: [qna, ...(product.value.qnas || [])],
    }
    qnaForm.question = ''
    qnaForm.secret = false
    showToast('문의를 등록했습니다.')
  } catch (error) {
    showToast(error instanceof Error ? error.message : '문의 등록에 실패했습니다.')
  } finally {
    qnaBusy.value = false
  }
}

const gallery = computed(() => {
  if (!product.value) return []
  const list = product.value.images?.length ? product.value.images : [product.value.image]
  return list.filter(Boolean)
})

const activeImage = computed(() => gallery.value[activeImageIndex.value] ?? product.value?.image ?? '')

const detailBlocks = computed(() => product.value?.blocks ?? [])

type SelectedLine = {
  key: string
  optionName: string
  optionExtra: number
  qty: number
}

/* 옵션은 하나만 고른다. 수량은 그 옵션만 올리고, 옵션을 바꾸면 1부터 다시 센다. */
const optionPick = ref('')
const selectedLines = ref<SelectedLine[]>([])

/* 상품은 항상 옵션을 가진다. 한 개면 옵션 1개 상품이다. */
const productOptions = computed(() =>
  (product.value?.options ?? []).filter((option) => option.use !== false && option.name.trim()),
)

const optionGroups = computed(() => {
  const groups = new Map<string, MallProductOption[]>()

  productOptions.value.forEach((option) => {
    const groupName = option.group?.trim() || '구성'
    const list = groups.get(groupName) ?? []
    list.push(option)
    groups.set(groupName, list)
  })

  return [...groups.entries()].map(([name, items]) => ({ name, items }))
})

function choiceMoneyLabel(name: string, extra: number) {
  const total = Math.max(0, (Number(product.value?.price) || 0) + (Number(extra) || 0))
  const qty = optionPieceCount(name)
  const totalText = `${formatPrice(total)}원`
  if (qty === 1) return `${name} ${totalText}`
  if (qty && qty > 1) return `${name} ${totalText} · 개당 ${formatPrice(Math.round(total / qty))}원`
  if (extra) return `${name} (${extra > 0 ? '+' : ''}${formatPrice(extra)}원)`
  return name
}

const flatOptionChoices = computed(() => {
  const groups = optionGroups.value
  if (!groups.length) return [] as Array<{ name: string; label: string; extra: number; stock: number }>

  const flatItems = groups.flatMap((group) => group.items)
  const quantityList =
    flatItems.length > 0 && flatItems.every((option) => optionPieceCount(option.name) != null)
  if (groups.length === 1 || quantityList) {
    return (quantityList ? flatItems : groups[0].items)
      .map((option) => ({
        name: option.name,
        label: choiceMoneyLabel(option.name, option.extra),
        extra: option.extra,
        stock: option.stock,
      }))
      .sort((a, b) => (optionPieceCount(a.name) ?? 9999) - (optionPieceCount(b.name) ?? 9999))
  }

  /* 그룹이 여러 개면 "구성 / 사이즈"처럼 조합 라벨로 펼친다 */
  const [first, ...rest] = groups
  let combos = first.items.map((option) => ({
    parts: [option.name],
    extra: option.extra,
    stock: option.stock,
  }))
  rest.forEach((group) => {
    const next: typeof combos = []
    combos.forEach((combo) => {
      group.items.forEach((option) => {
        next.push({
          parts: [...combo.parts, option.name],
          extra: combo.extra + option.extra,
          stock: Math.min(combo.stock, option.stock),
        })
      })
    })
    combos = next
  })

  return combos.map((combo) => {
    const name = combo.parts.join(' / ')
    return {
      name,
      label: choiceMoneyLabel(name, combo.extra),
      extra: combo.extra,
      stock: combo.stock,
    }
  })
})

/* 주문·재고는 상품(mp_stock) 기준. 옵션 재고가 전부 0인 옛 데이터는 상품 판매중이면 선택 가능으로 본다. */
const hasBasePriceChoice = computed(() =>
  flatOptionChoices.value.some((item) => optionPieceCount(item.name) === 1 || item.extra === 0),
)

const tracksOptionStock = computed(() => {
  const choices = flatOptionChoices.value
  if (choices.length && choices.every((item) => optionPieceCount(item.name) != null)) return false
  return choices.some((item) => item.stock > 0)
})

function isChoiceAvailable(choice: { stock: number }) {
  if (!product.value || product.value.soldOut) return false
  if (!tracksOptionStock.value) return true
  return choice.stock > 0
}

function choiceLabel(choice: { name: string; label: string; stock: number }) {
  return isChoiceAvailable(choice) ? choice.label : `${choice.label} · 품절`
}

function seedDefaultLine() {
  selectedLines.value = []
  optionPick.value = ''
  if (!product.value) return

  /* 기본 선택은 1개. 1개가 없으면 판매가와 같은 옵션만 고른다. */
  const choices = flatOptionChoices.value
  const choice =
    choices.find((item) => optionPieceCount(item.name) === 1 && isChoiceAvailable(item)) ??
    choices.find((item) => optionPieceCount(item.name) === 1) ??
    choices.find((item) => item.extra === 0 && isChoiceAvailable(item)) ??
    choices.find((item) => item.extra === 0)
  if (!choice) return

  optionPick.value = choice.name
  if (!isChoiceAvailable(choice)) return

  selectedLines.value = [
    {
      key: `${product.value.id}::${choice.name}`,
      optionName: choice.name,
      optionExtra: choice.extra,
      qty: clampQty(minQty.value),
    },
  ]
}

watch(
  () => {
    if (!product.value) return ''
    const opts = productOptions.value
      .map((option) => `${option.group}:${option.name}:${option.extra}:${option.stock}`)
      .join('|')
    return `${product.value.id}::${opts}`
  },
  () => {
    seedDefaultLine()
  },
)

function onOptionPick() {
  const name = optionPick.value
  if (!name || !product.value) return
  const choice = flatOptionChoices.value.find((item) => item.name === name)
  if (!choice || !isChoiceAvailable(choice)) {
    /* 품절이면 이전 선택(기본값)으로 되돌린다 */
    const fallback =
      selectedLines.value[0]?.optionName ||
      flatOptionChoices.value.find((item) => isChoiceAvailable(item))?.name ||
      ''
    optionPick.value = fallback
    return
  }

  if (selectedLines.value[0]?.optionName === name) return

  selectedLines.value = [
    {
      key: `${product.value.id}::${name}`,
      optionName: name,
      optionExtra: choice.extra,
      qty: clampQty(1),
    },
  ]
}

function setLineQty(key: string, raw: string | number) {
  const line = selectedLines.value.find((item) => item.key === key)
  if (!line) return
  const parsed = typeof raw === 'number' ? raw : Number(String(raw).trim())
  if (!Number.isFinite(parsed)) {
    line.qty = clampQty(minQty.value)
    return
  }
  line.qty = clampQty(Math.trunc(parsed))
}

const goodsAmount = computed(() => {
  if (!product.value) return 0
  if (selectedLines.value.length) {
    return selectedLines.value.reduce(
      (sum, line) => sum + lineUnitPrice(line.optionExtra) * line.qty,
      0,
    )
  }
  return priced.value.unitPrice * qty.value
})

const pickQty = computed(() => {
  if (selectedLines.value.length) {
    return selectedLines.value.reduce((sum, line) => sum + line.qty, 0)
  }
  return qty.value
})

const orderShippingFee = computed(() => {
  if (!product.value) return 0
  return calcOrderShippingFromLines([toShippingLine(product.value, pickQty.value)])
})

/** 바로구매·장바구니와 같은 최종 결제 예정액(상품가 + 배송비) */
const totalAmount = computed(() => goodsAmount.value + orderShippingFee.value)

const priced = computed(() =>
  calcMallUnitPrice({
    listPrice: product.value?.listPrice,
    salePrice: product.value?.price ?? 0,
  }),
)

function lineUnitPrice(optionExtra = 0): number {
  if (!product.value) return 0
  return calcMallUnitPrice({
    listPrice: product.value.listPrice,
    salePrice: product.value.price,
    optionExtra,
  }).unitPrice
}

const wished = computed(() => (product.value ? isWished(product.value.id) : false))

function pickByCodes(codes: string[] | undefined, limit: number) {
  if (!codes?.length) return []
  const selfId = product.value?.id
  const catalog = new Map(getAllProducts().map((item) => [item.id, item]))
  return codes
    .map((id) => catalog.get(id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item) && item!.id !== selfId)
    .slice(0, limit)
}

const related = computed(() => {
  if (!product.value) return []
  const pinned = pickByCodes(product.value.relatedCodes, 8)
  if (pinned.length) return pinned
  return getAllProducts()
    .filter((item) => item.category === product.value!.category && item.id !== product.value!.id)
    .sort((a, b) => b.soldCount - a.soldCount)
    .slice(0, 8)
})

const alsoRecommend = computed(() => {
  if (!product.value) return []
  const skip = new Set([product.value.id, ...related.value.map((item) => item.id)])
  return getAllProducts()
    .filter((item) => !skip.has(item.id) && !item.soldOut)
    .sort((a, b) => b.soldCount - a.soldCount)
    .slice(0, 4)
})

const recentOthers = computed(() => {
  if (!product.value) return []
  const catalog = new Map(getAllProducts().map((item) => [item.id, item]))
  return recentIds.value
    .filter((id) => id !== product.value!.id)
    .map((id) => catalog.get(id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .slice(0, 4)
})

/* 품절 시 관리자 지정 대체품 → 없으면 같은 분류 재고 있는 상품 */
const substitutes = computed(() => {
  if (!product.value?.soldOut) return []
  const pinned = pickByCodes(product.value.substituteCodes, 4).filter((item) => !item.soldOut)
  if (pinned.length) return pinned
  return getAllProducts()
    .filter(
      (item) =>
        !item.soldOut &&
        item.category === product.value!.category &&
        item.id !== product.value!.id,
    )
    .sort((a, b) => b.soldCount - a.soldCount)
    .slice(0, 4)
})

const showToast = (message: string) => {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 2800)
}

const onWish = () => {
  if (!product.value || !ensureLoggedIn()) return
  const next = !isWished(product.value.id)
  toggleWish(product.value.id)
  showToast(next ? '찜했습니다.' : '찜을 해제했습니다.')
}

async function onShare() {
  if (!product.value) return
  const { shareMallProduct } = await import('@/utils/mallShare')
  const result = await shareMallProduct({
    title: product.value.name,
    text: `${product.value.name} — 함우리몰`,
    url: `${window.location.origin}/mall/${product.value.id}`,
  })
  if (result === 'copied') showToast('상품 주소를 복사했습니다.')
  else if (result === 'shared') showToast('공유했습니다.')
}

function ensurePurchaseReady(): boolean {
  if (!product.value) return false
  /* 셀렉트에 값이 있는데 줄이 비었으면(기본 시드 누락) 한 번 더 맞춘다 */
  if (!selectedLines.value.length && optionPick.value) {
    onOptionPick()
  }
  if (!selectedLines.value.length) {
    showToast('옵션을 선택해 주세요.')
    return false
  }
  return true
}

async function pushSelectedLines(mode: 'add' | 'set' = 'add') {
  if (!product.value) return [] as string[]
  const keys: string[] = []
  for (const line of selectedLines.value) {
    await addItem(
      product.value.id,
      line.qty,
      {
        name: line.optionName,
        extra: line.optionExtra,
      },
      mode,
    )
    keys.push(cartKey(product.value.id, line.optionName))
  }
  return keys
}

const addToCart = async () => {
  if (!product.value || !ensureLoggedIn() || !ensurePurchaseReady()) return
  try {
    await pushSelectedLines('add')
    showToast('장바구니에 담았습니다.')
  } catch (error) {
    showToast(error instanceof Error ? error.message : '장바구니에 담지 못했습니다.')
  }
}

const buyNow = async () => {
  if (!product.value || !ensureLoggedIn() || !ensurePurchaseReady()) return
  try {
    /* 지금 보는 상품·옵션만 결제. 장바구니 다른 상품은 주문에 넣지 않는다. */
    const keys = await pushSelectedLines('set')
    if (!keys.length) {
      showToast('옵션을 선택해 주세요.')
      return
    }
    setBuyNowKeys(keys)
    selectOnly(keys)
    router.push({ path: '/mall/checkout', query: { mode: 'buynow' } })
  } catch (error) {
    showToast(error instanceof Error ? error.message : '장바구니에 담지 못했습니다.')
  }
}

function panelId(id: TabId) {
  return `panel-${id}`
}

function stickyOffset() {
  const tabBar = tabsEl.value?.offsetHeight ?? 52
  return headerOffsetPx() + tabBar
}

function scrollToTab(id: TabId) {
  const el = document.getElementById(panelId(id))
  if (!el) return

  activeTab.value = id
  scrollingByClick = true
  window.clearTimeout(scrollUnlockTimer)

  const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset()
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })

  scrollUnlockTimer = setTimeout(() => {
    scrollingByClick = false
    syncActiveTabByScroll()
  }, 700)
}

function syncActiveTabByScroll() {
  if (scrollingByClick || !product.value) return

  const offset = stickyOffset()
  let current: TabId = tabs[0].id

  for (const tab of tabs) {
    const el = document.getElementById(panelId(tab.id))
    if (!el) continue
    if (el.getBoundingClientRect().top - offset <= 1) {
      current = tab.id
    }
  }

  if (activeTab.value !== current) {
    activeTab.value = current
  }
}

function onWindowScroll() {
  if (scrollSpyRaf) return
  scrollSpyRaf = window.requestAnimationFrame(() => {
    scrollSpyRaf = 0
    fitBuyPanelToViewport()
    syncActiveTabByScroll()
  })
}

function applyTabQuery(scroll = false) {
  const tab = String(route.query.tab ?? '')
  if (!tabs.some((item) => item.id === tab)) return
  const id = tab as TabId
  if (scroll) {
    void nextTick(() => scrollToTab(id))
  } else {
    activeTab.value = id
  }
}

function onWindowResize() {
  fitBuyPanelToViewport()
  syncGalleryHeight()
}

onMounted(() => {
  void loadSnippets([
    'mall.product.trust_badge',
    'mall.product.shipping_notes',
    'mall.product.return_policy',
    'mall.product.same_day_cutoff',
  ])
  void nextTick(() => bindGalleryHeightSync())
  window.addEventListener('scroll', onWindowScroll, { passive: true })
  window.addEventListener('resize', onWindowResize, { passive: true })
  window.visualViewport?.addEventListener('resize', onWindowResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onWindowScroll)
  window.removeEventListener('resize', onWindowResize)
  window.visualViewport?.removeEventListener('resize', onWindowResize)
  buyResizeObserver?.disconnect()
  buyResizeObserver = null
  window.clearTimeout(scrollUnlockTimer)
  if (scrollSpyRaf) window.cancelAnimationFrame(scrollSpyRaf)
})

watch(
  () => route.params.id,
  () => {
    qty.value = 1
    lastPurchaseQty.value = null
    selectedLines.value = []
    optionPick.value = ''
    activeTab.value = 'detail'
    toast.value = ''
    window.scrollTo({ top: 0, behavior: 'smooth' })
    loadProduct().then(() => {
      void nextTick(() => {
        bindGalleryHeightSync()
        applyTabQuery(true)
      })
    })
  },
)

watch(
  () => [product.value?.id, selectedLines.value.length, optionPick.value] as const,
  () => {
    void nextTick(() => {
      fitBuyPanelToViewport()
      syncGalleryHeight()
    })
  },
)

watch(isLoggedIn, (loggedIn) => {
  if (loggedIn && product.value) {
    void loadLastPurchase(product.value.id)
    void loadQnaOrders()
  } else {
    lastPurchaseQty.value = null
    qnaOrders.value = []
    qnaForm.orderCode = ''
    qnaForm.kind = ''
  }
})

watch(
  () => route.query.tab,
  () => {
    applyTabQuery(true)
  },
)

watch(product, (value) => {
  if (!value) return
  void nextTick(() => syncActiveTabByScroll())
})
</script>

<style lang="scss" src="./MallProductPage.scss"></style>
